import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDocs,
    getFirestore,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
} from './instrumentedFirestore';
import { getFirebaseApp } from './app';
import { COLLECTIONS } from '@constants/firestorePaths';

const ITEMS = 'items';

function db() {
    return getFirestore(getFirebaseApp());
}

function stripUndefined(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    const next = {};
    for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'undefined') continue;
        next[k] = v;
    }
    return next;
}

function tenantSubjectItemsCollectionRef(tenantId) {
    return collection(db(), COLLECTIONS.TENANT_SUBJECTS, tenantId, ITEMS);
}

function tenantSubjectsSummaryRef(tenantId) {
    return doc(db(), COLLECTIONS.TENANT_SUBJECTS, tenantId);
}

async function refreshTenantSubjectsSummary(tenantId) {
    const snap = await getDocs(query(tenantSubjectItemsCollectionRef(tenantId), orderBy('createdAt', 'desc')));
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    let withTeacher = 0;
    for (const r of rows) {
        if (r.assignedTeacherId && String(r.assignedTeacherId).trim()) withTeacher += 1;
    }
    await setDoc(
        tenantSubjectsSummaryRef(tenantId),
        {
            tenantId,
            totalSubjects: rows.length,
            subjectsWithAssignedTeacher: withTeacher,
            subjectsWithoutTeacher: Math.max(0, rows.length - withTeacher),
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
}

/** tenantSubjects/{tenantId}/items — subject documents */
export function subscribeTenantSubjects({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const ref = tenantSubjectItemsCollectionRef(tenantId);
    const q = query(ref, orderBy('createdAt', 'desc'));
    return onSnapshot(
        q,
        (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            onNext?.(rows);
        },
        (err) => onError?.(err)
    );
}

export async function createTenantSubject({ tenantId, payload }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const ref = tenantSubjectItemsCollectionRef(tenantId);

    const docPayload = stripUndefined({
        ...payload,
        tenantId,
        classIds: Array.isArray(payload?.classIds) ? payload.classIds : [],
        assignedTeacherId: payload?.assignedTeacherId || null,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
    });

    const created = await addDoc(ref, docPayload);
    await refreshTenantSubjectsSummary(tenantId);
    return { id: created.id };
}

export async function updateTenantSubject({ tenantId, subjectId, payload }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!subjectId) throw new Error('Missing subjectId');
    const ref = doc(db(), COLLECTIONS.TENANT_SUBJECTS, tenantId, ITEMS, subjectId);
    const next = stripUndefined({
        ...payload,
        tenantId,
        classIds: Array.isArray(payload?.classIds) ? payload.classIds : undefined,
        assignedTeacherId:
            payload && Object.prototype.hasOwnProperty.call(payload, 'assignedTeacherId')
                ? payload.assignedTeacherId || null
                : undefined,
        updatedAt: serverTimestamp(),
    });
    await updateDoc(ref, next);
    await refreshTenantSubjectsSummary(tenantId);
    return { id: subjectId };
}

export async function deleteTenantSubject({ tenantId, subjectId }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!subjectId) throw new Error('Missing subjectId');
    const ref = doc(db(), COLLECTIONS.TENANT_SUBJECTS, tenantId, ITEMS, subjectId);
    await deleteDoc(ref);
    await refreshTenantSubjectsSummary(tenantId);
    return { id: subjectId };
}
